import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

interface PerformanceMetric {
  routeName: string;
  routeType: 'angular-router';
  startTime: number;
  endTime?: number;
  duration?: number;
  parameters?: any;
  success: boolean;
  error?: string;
  timestamp: Date;
  // Sircon-specific metrics
  routeCategory: string; // 'network', 'dashboard', 'auth', etc.
  requiresPermission?: boolean;
  userContextLoadTime?: number;
  permissionCheckDuration?: number;
  isWorkflowRoute?: boolean;
  hasMultipleViews?: boolean;
  networkId?: string; // For network-specific routes
}

interface PerformanceStats {
  totalNavigations: number;
  averageDuration: number;
  fastestNavigation: number;
  slowestNavigation: number;
  successRate: number;
  errorCount: number;
  routeBreakdown: { [routeName: string]: PerformanceMetric[] };
}

// Commented out as unused in current implementation but kept for future metrics enhancement
// interface SirconPerformanceStats extends PerformanceStats {
//   // Sircon-specific statistics
//   categoryBreakdown: { [category: string]: PerformanceStats };
//   permissionCheckStats: {
//     averageCheckTime: number;
//     checkSuccessRate: number;
//     userContextLoadFailures: number;
//   };
//   workflowRouteStats: {
//     averageDuration: number;
//     completionRate: number;
//     mostProblematicWorkflows: string[];
//   };
//   networkRouteStats: {
//     averageDuration: number;
//     mostActiveNetworks: string[];
//     entityRoutePerformance: number;
//   };
// }

@Injectable({
  providedIn: 'root'
})
export class RoutingPerformanceMonitorService {
  private metrics: PerformanceMetric[] = [];
  private currentNavigation: PerformanceMetric | null = null;
  private readonly STORAGE_KEY = 'routing-performance-metrics';

  constructor(
    private router: Router
  ) {
    this.initializeAngularRouterMonitoring();
    this.loadStoredMetrics();
  }

  /**
   * Initialize performance monitoring for Angular Router
   */
  private initializeAngularRouterMonitoring(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe((event: NavigationStart) => {
      this.startNavigationTracking(event.url, {});
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.endNavigationTracking(true);
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationCancel || event instanceof NavigationError)
    ).subscribe((event: NavigationCancel | NavigationError) => {
      const error = event instanceof NavigationError ? event.error : 'Navigation cancelled';
      this.endNavigationTracking(false, error);
    });
  }


  /**
   * Start tracking a navigation with Sircon-specific data
   */
  private startNavigationTracking(routeName: string, parameters: any): void {
    this.currentNavigation = {
      routeName,
      routeType: 'angular-router',
      startTime: performance.now(),
      parameters,
      success: false,
      timestamp: new Date(),
      // Sircon-specific categorization
      routeCategory: this.categorizeRoute(routeName),
      requiresPermission: this.routeRequiresPermission(routeName),
      isWorkflowRoute: this.isWorkflowRoute(routeName),
      hasMultipleViews: this.hasMultipleViews(routeName),
      networkId: this.extractNetworkId(parameters)
    };
  }

  /**
   * Categorize routes by Sircon business function
   */
  private categorizeRoute(routeName: string): string {
    if (routeName.includes('login') || routeName.includes('auth') || routeName.includes('sso')) {
      return 'authentication';
    }
    if (routeName.startsWith('network')) {
      return 'network';
    }
    if (routeName.startsWith('dashboard')) {
      return 'dashboard';
    }
    if (routeName.startsWith('profile')) {
      return 'profile';
    }
    if (routeName.startsWith('admin') || routeName.startsWith('configuration')) {
      return 'admin';
    }
    if (routeName.includes('workflow') || routeName.includes('filing') || routeName.includes('interview')) {
      return 'workflow';
    }
    if (routeName.includes('contract')) {
      return 'contracts';
    }
    if (routeName.startsWith('signup') || routeName.startsWith('invite')) {
      return 'signup';
    }
    return 'other';
  }

  /**
   * Check if route requires permission based on Sircon patterns
   */
  private routeRequiresPermission(routeName: string): boolean {
    // Most Sircon routes require permission except login/public routes
    const publicRoutes = ['login', 'sso', 'forgot', 'reset', 'error', 'redirect'];
    return !publicRoutes.some(route => routeName.includes(route));
  }

  /**
   * Check if route is a workflow route (typically slower)
   */
  private isWorkflowRoute(routeName: string): boolean {
    const workflowPatterns = ['workflow', 'filing', 'interview', 'carrier-connect', 'onboarding'];
    return workflowPatterns.some(pattern => routeName.includes(pattern));
  }

  /**
   * Check if route uses multiple views (header, sideRail, content, footer)
   */
  private hasMultipleViews(routeName: string): boolean {
    // Most Sircon routes use multiple views except simple ones
    const singleViewRoutes = ['error', 'login', 'redirect'];
    return !singleViewRoutes.some(route => routeName.includes(route));
  }

  /**
   * Extract network ID from route parameters
   */
  private extractNetworkId(parameters: any): string | undefined {
    if (parameters && typeof parameters === 'object') {
      return parameters.networkId || parameters.carrierNetworkId;
    }
    return undefined;
  }

  /**
   * End tracking a navigation
   */
  private endNavigationTracking(success: boolean, error?: string): void {
    if (this.currentNavigation) {
      const endTime = performance.now();
      this.currentNavigation.endTime = endTime;
      this.currentNavigation.duration = endTime - this.currentNavigation.startTime;
      this.currentNavigation.success = success;

      if (error) {
        this.currentNavigation.error = error;
      }

      this.metrics.push({ ...this.currentNavigation });
      this.saveMetricsToStorage();
      this.currentNavigation = null;

      // Log performance if it's unusually slow
      if (this.currentNavigation?.duration && this.currentNavigation.duration > 2000) {
        console.warn(`Slow navigation detected: ${this.currentNavigation.routeName} took ${this.currentNavigation.duration}ms`);
      }
    }
  }

  /**
   * Get comprehensive performance statistics
   */
  getPerformanceStats(): PerformanceStats {
    if (this.metrics.length === 0) {
      return {
        totalNavigations: 0,
        averageDuration: 0,
        fastestNavigation: 0,
        slowestNavigation: 0,
        successRate: 0,
        errorCount: 0,
        routeBreakdown: {}
      };
    }

    const successfulNavigations = this.metrics.filter(m => m.success && m.duration !== undefined);
    const durations = successfulNavigations.map(m => m.duration!);
    const routeBreakdown: { [routeName: string]: PerformanceMetric[] } = {};

    // Group metrics by route name
    this.metrics.forEach(metric => {
      if (!routeBreakdown[metric.routeName]) {
        routeBreakdown[metric.routeName] = [];
      }
      routeBreakdown[metric.routeName].push(metric);
    });

    return {
      totalNavigations: this.metrics.length,
      averageDuration: durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0,
      fastestNavigation: durations.length > 0 ? Math.min(...durations) : 0,
      slowestNavigation: durations.length > 0 ? Math.max(...durations) : 0,
      successRate: (successfulNavigations.length / this.metrics.length) * 100,
      errorCount: this.metrics.filter(m => !m.success).length,
      routeBreakdown
    };
  }


  /**
   * Calculate statistics for a subset of metrics
   */
  private calculateStatsForMetrics(metrics: PerformanceMetric[]): PerformanceStats {
    if (metrics.length === 0) {
      return {
        totalNavigations: 0,
        averageDuration: 0,
        fastestNavigation: 0,
        slowestNavigation: 0,
        successRate: 0,
        errorCount: 0,
        routeBreakdown: {}
      };
    }

    const successfulNavigations = metrics.filter(m => m.success && m.duration !== undefined);
    const durations = successfulNavigations.map(m => m.duration!);
    const routeBreakdown: { [routeName: string]: PerformanceMetric[] } = {};

    metrics.forEach(metric => {
      if (!routeBreakdown[metric.routeName]) {
        routeBreakdown[metric.routeName] = [];
      }
      routeBreakdown[metric.routeName].push(metric);
    });

    return {
      totalNavigations: metrics.length,
      averageDuration: durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0,
      fastestNavigation: durations.length > 0 ? Math.min(...durations) : 0,
      slowestNavigation: durations.length > 0 ? Math.max(...durations) : 0,
      successRate: (successfulNavigations.length / metrics.length) * 100,
      errorCount: metrics.filter(m => !m.success).length,
      routeBreakdown
    };
  }

  /**
   * Get the top slowest routes
   */
  getSlowestRoutes(limit: number = 10): Array<{ routeName: string; averageDuration: number; navigationCount: number }> {
    const stats = this.getPerformanceStats();
    const routeStats = Object.keys(stats.routeBreakdown).map(routeName => {
      const routeMetrics = stats.routeBreakdown[routeName].filter(m => m.success && m.duration !== undefined);
      const durations = routeMetrics.map(m => m.duration!);

      return {
        routeName,
        averageDuration: durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0,
        navigationCount: routeMetrics.length
      };
    });

    return routeStats
      .sort((a, b) => b.averageDuration - a.averageDuration)
      .slice(0, limit);
  }

  /**
   * Export performance data for analysis
   */
  exportPerformanceData(): string {
    const stats = this.getPerformanceStats();
    const slowestRoutes = this.getSlowestRoutes();

    return JSON.stringify({
      generatedAt: new Date().toISOString(),
      overallStats: stats,
      slowestRoutes: slowestRoutes,
      rawMetrics: this.metrics
    }, null, 2);
  }

  /**
   * Clear all performance metrics
   */
  clearMetrics(): void {
    this.metrics = [];
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Load metrics from localStorage
   */
  private loadStoredMetrics(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.metrics = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load stored routing metrics:', error);
    }
  }

  /**
   * Save metrics to localStorage
   */
  private saveMetricsToStorage(): void {
    try {
      // Keep only the last 1000 metrics to prevent excessive storage usage
      const metricsToStore = this.metrics.slice(-1000);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(metricsToStore));
    } catch (error) {
      console.warn('Failed to save routing metrics:', error);
    }
  }

  /**
   * Generate performance report
   */
  generatePerformanceReport(): string {
    const stats = this.getPerformanceStats();
    const slowestRoutes = this.getSlowestRoutes(5);

    return `
# Routing Performance Report
Generated: ${new Date().toISOString()}

## Overall Statistics
- Total Navigations: ${stats.totalNavigations}
- Average Duration: ${stats.averageDuration.toFixed(2)}ms
- Success Rate: ${stats.successRate.toFixed(2)}%
- Error Count: ${stats.errorCount}

## Slowest Routes
${slowestRoutes.map((route, index) =>
  `${index + 1}. ${route.routeName}: ${route.averageDuration.toFixed(2)}ms (${route.navigationCount} navigations)`
).join('\n')}
    `;
  }
}
